import { isDefined } from "@clipboard-health/util-ts";
import { useMemo } from "react";

import { useWorkplaceOpenShiftsData } from "../../../Workplace/OpenShifts/useWorkplaceOpenShiftsData";
import type { ModalWorkplaceData } from "../../types";
import { computeDoubleShiftPays } from "../computeDoubleShiftPays";
import { groupShiftsIntoDoubles } from "../groupShiftsIntoDoubles";
import type { ShiftDouble } from "../types";
import { useShiftDoublesEnabled } from "../useShiftDoublesEnabled";

interface UseDoubleForShiftProps {
  shiftId: string | undefined;
  workplace: ModalWorkplaceData | undefined;
  enabled: boolean;
}

interface UseDoubleForShiftResult {
  double: ShiftDouble | undefined;
  isLoading: boolean;
}

/**
 * Hook to fetch and calculate a double for a specific shift.
 * Use this when double data is not available in context (e.g., returning from quiz flow).
 */
export function useDoubleForShift(props: UseDoubleForShiftProps): UseDoubleForShiftResult {
  const { shiftId, workplace, enabled } = props;

  const isDoublesEnabled = useShiftDoublesEnabled();
  const shouldFetch = enabled && isDoublesEnabled && isDefined(shiftId) && isDefined(workplace);

  const {
    data: { unfilteredWorkplaceShifts: workplaceShifts },
    offersMap,
    isLoading,
  } = useWorkplaceOpenShiftsData(workplace, { enabled: shouldFetch });

  const requiresLunchBreak = workplace?.attributes.requiresLunchBreak ?? false;

  const double = useMemo(() => {
    if (!shouldFetch || !shiftId || isLoading) {
      return undefined;
    }

    const doublesByShiftId = groupShiftsIntoDoubles(workplaceShifts);
    const foundDouble = doublesByShiftId.get(shiftId);

    if (!isDefined(foundDouble)) {
      return undefined;
    }

    const anchorShift = workplaceShifts.find((s) => s.id === shiftId);

    if (!isDefined(anchorShift)) {
      return foundDouble;
    }

    const anchorOfferId = anchorShift.relationships.offer?.data.id;
    const anchorOffer = anchorOfferId ? offersMap.get(anchorOfferId) : undefined;

    return {
      ...foundDouble,
      ...computeDoubleShiftPays({
        double: foundDouble,
        anchorShift: { start: anchorShift.attributes.start, end: anchorShift.attributes.end },
        anchorOffer,
        offersMap,
        requiresLunchBreak,
      }),
    };
  }, [shouldFetch, shiftId, workplaceShifts, offersMap, requiresLunchBreak, isLoading]);

  return {
    double,
    isLoading,
  };
}
